import type { PlanEvent, Task } from "../../../shared/types";
import type { HolidayEntry } from "../../../shared/hooks/use-holidays";
import { getCategoryPillColor, getCategoryDotColor } from "../../../shared/stores/category-store";
import {
  getDayName,
  getDayNumber,
  isSameMonth,
} from "../../../shared/types/date-helpers";

interface MonthGridProps {
  readonly days: readonly string[];
  readonly currentDate: string;
  readonly today: string;
  readonly selectedDate: string | null;
  readonly eventsByDate: ReadonlyMap<string, PlanEvent[]>;
  readonly tasksByDate: ReadonlyMap<string, Task[]>;
  readonly onDayClick: (date: string) => void;
  readonly holidays?: ReadonlyMap<string, HolidayEntry>;
}

const WEEK_DAY_LABELS = ["LUN", "MAR", "MER", "JEU", "VEN", "SAM", "DIM"];

export function MonthGrid({
  days,
  currentDate,
  today,
  selectedDate,
  eventsByDate,
  tasksByDate,
  onDayClick,
  holidays,
}: MonthGridProps) {
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Fixed day-of-week headers */}
      <div
        className="grid grid-cols-7 border-b border-border/15"
        role="row"
      >
        {WEEK_DAY_LABELS.map((d) => (
          <div
            key={d}
            className="py-2 text-center text-[11px] font-medium text-ink-faint tracking-wide"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div
        className="grid grid-cols-7 flex-1 min-h-0"
        role="grid"
        aria-label="Calendrier mois"
      >
        {days.map((day) => {
          const isToday = day === today;
          const isSelected = day === selectedDate;
          const inMonth = isSameMonth(day, currentDate);
          const dayEvents = eventsByDate.get(day) ?? [];
          const dayTasks = tasksByDate.get(day) ?? [];
          const previewEvents = dayEvents.slice(0, 3);
          const taskCount = dayTasks.length;

          return (
            <button
              key={day}
              type="button"
              onClick={() => onDayClick(day)}
              aria-label={`${getDayName(day)} ${getDayNumber(day)}${dayEvents.length > 0 ? `, ${dayEvents.length} ev.` : ""}${taskCount > 0 ? `, ${taskCount} tâches` : ""}`}
              className={`flex flex-col gap-0.5 p-1.5 min-h-[80px] text-left border-b border-r border-border/10
                focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent/30
                transition-colors
                ${isSelected ? "bg-accent/[0.06]" : "hover:bg-surface-alt/30 active:bg-surface-alt/50"}
                ${!inMonth ? "opacity-40" : ""}`}
            >
              {/* Day number */}
              <div className="flex justify-center mb-0.5">
                <span
                  className={`text-sm leading-none w-7 h-7 flex items-center justify-center rounded-full
                    ${isToday ? "bg-accent text-white font-medium" : "text-ink font-normal"}`}
                >
                  {getDayNumber(day)}
                </span>
              </div>

              {/* Holiday label */}
              {holidays?.get(day) && (
                <span
                  className={`text-[9px] font-medium truncate w-full text-center leading-tight ${holidays.get(day)!.official ? "text-red-500 dark:text-red-400" : "text-ink-faint"}`}
                  title={holidays.get(day)!.official ? `${holidays.get(day)!.name} — Jour férié` : holidays.get(day)!.name}
                >
                  {holidays.get(day)!.name}
                </span>
              )}

              {/* Event pills */}
              <div className="flex flex-col gap-px w-full min-h-0 overflow-hidden">
                {previewEvents.map((evt) => (
                  <span
                    key={evt.id}
                    className={`text-[10px] font-medium truncate w-full leading-snug rounded px-1.5 py-px ${getCategoryPillColor(evt.category)}`}
                  >
                    {evt.allDay ? "" : `${evt.startTime.slice(0, 5)} `}{evt.title}
                  </span>
                ))}
                {dayEvents.length > 3 && (
                  <span className="text-[10px] text-ink-faint leading-tight px-1.5 font-medium">
                    +{dayEvents.length - 3} autres
                  </span>
                )}
                {dayEvents.length === 0 && taskCount > 0 && (
                  <div className="flex gap-1 flex-wrap px-0.5 mt-0.5">
                    {Array.from(new Set(dayTasks.map((t) => t.category))).map(
                      (cat) => (
                        <span
                          key={cat}
                          className={`w-2 h-2 rounded-full ${getCategoryDotColor(cat)}`}
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
